import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from "@angular/core";
import { TranslationService } from "@app/services/translation.service";
import "./v-slider.component.scss";

interface ISliderHandle {
    dragging: boolean;
    value: number;
    valueIndex: number;
    leftPercentage: number;
    label?: HTMLElement;
    labelTop: number;
    maxWidth?: string;
    mirrored?: boolean;
    hideGrip?: boolean;
}

export interface ISliderConfig {
    zoneColors: string[];
    zoneLabels: string[];
}

@Component({
    selector: "v-slider",
    template: require('./v-slider.component.html')
})
export class VSliderComponent implements OnInit, AfterViewInit {

    @Input() values: number[];
    @Output() valuesChange = new EventEmitter<number[]>();

    @Input() showLabels: boolean;
    @Input() showMinLabel: boolean;
    @Input() showLabelsAsRange: boolean;
    @Input() config: ISliderConfig;

    @Input() unit: number | any;

    @Input() min: number = 0;
    @Input() max: number = 100;

    @ViewChild("sliderRail", { static: true }) sliderRail: ElementRef;
    @ViewChild("keyContainer", { static: false }) keyContainer: ElementRef;

    hasActiveDrags: boolean;
    handles: ISliderHandle[];
    selectedHandle: ISliderHandle;
    totalZones: number = 0;
    readonly sliderKeyPrefix = "v-slider-key-";

    constructor(private readonly translationService: TranslationService) { }

    ngOnInit(): void {
        this.render();
    }

    ngAfterViewInit(): void {
        this.updateHandleLabels();
    }

    @HostListener("document:mouseup", ["$event"])
    onAnyMouseUp(event: MouseEvent): void {

        if (!this.hasActiveDrags) {
            this.clearSelectHandle();
        }

        this.clearAllHandleDragFlags();
    }

    @HostListener("document:mousemove", ["$event"])
    onAnyMouseMove(event: MouseEvent): void {
        if (!this.hasActiveDrags) {
            return;
        }

        this.onHandleMoved(event);
    }

    @HostListener("document:keyup", ["$event"])
    onKeyUp(event: KeyboardEvent): void {
        if (!this.selectedHandle || !event.code) {
            return;
        }

        switch (event.code) {
            case "ArrowRight": this.moveHandleX(this.selectedHandle, 1);
                break;
            case "ArrowLeft": this.moveHandleX(this.selectedHandle, -1);
                break;
            default: this.clearSelectHandle();
                return;
        }
    }

    onStartDragHandle(event: MouseEvent, handle: ISliderHandle): void {
        handle.dragging = true;

        window.getSelection()?.removeAllRanges();

        this.selectHandle(handle);
        this.detectActiveDrags();
    }

    onEndDragHandle(event: MouseEvent, handle: ISliderHandle): void {
        setTimeout(() => {
            handle.dragging = false;

            this.selectHandle(handle);
            this.detectActiveDrags();
        });
    }

    onHandleMoved(event: MouseEvent): void {
        if (!this.hasActiveDrags) {
            return;
        }

        this.getDraggingHandles().forEach(handle => this.processMove(event.clientX, handle));
    }

    onHandleClicked(handle: ISliderHandle): void {
        this.selectHandle(handle);
    }

    moveHandleX(handle: ISliderHandle, x: number): void {
        handle.value = Math.max(this.min, Math.min((handle.value += x), this.max));
        handle.leftPercentage = this.getLeftPercentageForValue(handle.value);

        this.updateHandleLabels();
        this.propagateHandleChanges();
    }

    // ---

    private render(): void {
        if (!this.values?.length) {
            return;
        }

        this.generateHandles();
        this.generateConfig();
        this.updateHandleLabels();
    }

    private selectHandle(handle: ISliderHandle): void {
        this.selectedHandle = handle;
    }

    private clearSelectHandle(): void {
        this.selectedHandle = null;
    }

    private generateHandles(): void {
        this.handles = [];

        if (!this.values?.length) {
            console.warn("Slider has no values passed to input");
            return;
        }

        this.values.forEach((value, index) => {
            this.handles.push({
                dragging: false,
                value: Math.max(this.min, Math.min(value, this.max)),
                valueIndex: index,
                leftPercentage: this.getLeftPercentageForValue(value),
                labelTop: 0
            });
        });

        this.handles.unshift({
            dragging: false,
            value: this.min,
            valueIndex: null,
            leftPercentage: 0,
            labelTop: 0,
            hideGrip: true
        });

    }

    private getLeftPercentageForValue(value: number): number {
        return ((value - this.min) / (this.max - this.min)) * 100;
    }

    private processMove(x: number, handle: ISliderHandle): void {
        window.getSelection()?.removeAllRanges();

        const parentElement: HTMLElement = this.sliderRail.nativeElement as HTMLElement;
        const sliderRect = parentElement.getBoundingClientRect();
        const sliderWidth: number = parentElement.offsetWidth;

        const movePositionInsideSlider = x - sliderRect.left;
        const newLeftPercentage: number = +((movePositionInsideSlider / sliderWidth) * 100);
        handle.leftPercentage = Math.max(0, Math.min(newLeftPercentage, 100));

        const newValue = ((handle.leftPercentage / 100) * (this.max - this.min)) + this.min;
        handle.value = Math.max(this.min, Math.min(newValue, this.max));

        this.updateHandleLabels();
        this.propagateHandleChanges();
    }

    private clearAllHandleDragFlags(): void {
        if (!this.handles?.length) {
            return;
        }

        this.handles.forEach(handle => handle.dragging = false);
        this.hasActiveDrags = false;
    }

    private detectActiveDrags(): void {
        const activeHandles: ISliderHandle[] = this.getDraggingHandles();

        this.hasActiveDrags = activeHandles?.length > 0;
    }

    private getDraggingHandles(): ISliderHandle[] {
        return this.handles?.filter(handle => handle.dragging);
    }

    private propagateHandleChanges(): void {
        this.orderHandlesByValue();
    }

    private orderHandlesByValue(): void {
        if (!this.handles?.length) {
            return;
        }

        this.handles.sort((handle1, handle2) => handle1.value - handle2.value);
        this.handles.forEach((handle, index) => this.values[index] = handle.value);

        const exportValues = [...this.values];

        if (this.showMinLabel) {
            exportValues.splice(0, 1);
        }

        this.valuesChange.emit(exportValues);
    }

    private generateConfig(): void {
        if (!!this.config) {
            return;
        }

        this.config = {
            zoneColors: this.getDefaultColors(),
            zoneLabels: this.getDefaultLabels()
        };
    }

    private getDefaultColors(): string[] {
        switch (this.values.length) {
            case 1: return ["#004c93"];
            case 2: return ["#eb6a6a", "#ebb76a", "#78bb5d"];
            default: return ["#004c93"];
        }
    }

    private getDefaultLabels(): string[] {
        switch (this.values.length) {
            case 1: return [
                this.translationService.t("VSlider.ValueLabel")
            ];
            case 2: return [
                this.translationService.t("VSlider.PoorLabel"),
                this.translationService.t("VSlider.AverageLabel"),
                this.translationService.t("VSlider.GreatLabel")
            ];
            default: return [
                this.translationService.t("VSlider.ValueLabel")
            ];
        }
    }

    private updateHandleLabels(): void {

        if (!this.keyContainer?.nativeElement) {
            return;
        }

        const keyElements: HTMLElement[] = Array.from(this.keyContainer.nativeElement.querySelectorAll(".key"));

        if (!keyElements?.length) {
            return;
        }

        for (var i = keyElements.length - 1; i >= 0; i--) {
            const currentElement = keyElements[i];
            const currentElementRect = currentElement.getBoundingClientRect();

            if (!this.handles[i]) {
                continue;
            }

            this.handles[i].maxWidth = `100%`;

            if (i === keyElements.length - 1) {
                continue;
            }

            const closestNextSibling: HTMLElement = keyElements[i + 1];
            const closestNextSiblingRect: DOMRect = closestNextSibling.getBoundingClientRect();

            const newMaxWidth = closestNextSiblingRect.x - (currentElementRect.x);

            this.handles[i].maxWidth = `${newMaxWidth}px`;
        }
    }
}
